import { GygQueryOptions } from '@/src/views/components/master-pricing-list/types'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export interface FoodSafetyTag {
  id: number
  name: string
  description: string
  imageUrl?: string
  color?: string
  createdAt: string
  updatedAt: string
}

export interface FoodSafetyTagResponse {
  data: FoodSafetyTag
}

export interface FoodSafetyTagsResponse {
  data: FoodSafetyTag[]
  meta: {
    total: number
    pages: number
  }
}

export interface FoodSafetyTagStats {
  totalTags: number
  tagsWithImage: number
}

interface FoodSafetyTagInitialState {
  tags: FoodSafetyTag[]
  tag: FoodSafetyTag | null
  stats: FoodSafetyTagStats
  loading: boolean
  meta: FoodSafetyTagsResponse['meta']
  error: any
  success: string | null
}

const initialState: FoodSafetyTagInitialState = {
  tags: [],
  tag: null,
  stats: {
    totalTags: 0,
    tagsWithImage: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

export const getFoodSafetyTags = createAsyncThunk<
  FoodSafetyTagsResponse,
  GygQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>('gygFoodSafetyTags/getFoodSafetyTags', async (params, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`food-safety-tags`, {
      params
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const createFoodSafetyTag = createAsyncThunk<
  FoodSafetyTagResponse,
  FormData,
  { rejectValue: any; extra: { httpClient: any } }
>('gygFoodSafetyTags/createFoodSafetyTag', async (formData, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post('food-safety-tags', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const updateFoodSafetyTag = createAsyncThunk<
  FoodSafetyTagResponse,
  { id: number; data: FormData },
  { rejectValue: any; extra: { httpClient: any } }
>('gygFoodSafetyTags/updateFoodSafetyTag', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.patch(`food-safety-tags/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getFoodSafetyTag = createAsyncThunk<
  FoodSafetyTagResponse,
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygFoodSafetyTags/getFoodSafetyTag', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`food-safety-tags/${id}`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const deleteFoodSafetyTag = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygFoodSafetyTags/deleteFoodSafetyTag', async (id, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`food-safety-tags/${id}`)

    return { id }
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getFoodSafetyTagStats = createAsyncThunk<
  FoodSafetyTagStats,
  void,
  { rejectValue: any; extra: { httpClient: any } }
>('gygFoodSafetyTags/getFoodSafetyTagStats', async (_, { rejectWithValue, extra }) => {
  try {
    const { data: statsData } = await extra.httpClient.get('food-safety-tags/stats')

    return statsData.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

const gygFoodSafetyTagSlice = createSlice({
  name: 'gygFoodSafetyTags',
  initialState,
  reducers: {
    resetSuccessAndError: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getFoodSafetyTags.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getFoodSafetyTags.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.success = null
      state.tags = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getFoodSafetyTags.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch hazard tags'
    })

    builder.addCase(createFoodSafetyTag.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(createFoodSafetyTag.fulfilled, (state, action) => {
      state.loading = false

      // Ensure the created tag has a valid ID before adding to the array
      if (action.payload.data && action.payload.data.id) {
        state.tags.unshift(action.payload.data)
        state.meta.total += 1
      }
      state.success = 'Hazard tag created successfully'
    })
    builder.addCase(createFoodSafetyTag.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to create hazard tag'
    })

    builder.addCase(updateFoodSafetyTag.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateFoodSafetyTag.fulfilled, state => {
      state.loading = false
      state.success = 'Hazard tag updated successfully'
    })
    builder.addCase(updateFoodSafetyTag.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to update hazard tag'
    })

    builder.addCase(getFoodSafetyTag.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getFoodSafetyTag.fulfilled, (state, action) => {
      state.loading = false
      state.tag = action.payload.data
    })
    builder.addCase(getFoodSafetyTag.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch hazard tag'
    })

    builder.addCase(deleteFoodSafetyTag.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(deleteFoodSafetyTag.fulfilled, (state, action) => {
      state.loading = false
      state.tags = state.tags.filter(t => t.id !== action.payload.id)
      state.meta.total -= 1
      state.success = 'Hazard tag deleted successfully'
    })
    builder.addCase(deleteFoodSafetyTag.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to delete hazard tag'
    })
    builder.addCase(getFoodSafetyTagStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getFoodSafetyTagStats.fulfilled, (state, action) => {
      state.loading = false
      state.stats = action.payload
    })
    builder.addCase(getFoodSafetyTagStats.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch hazard tag stats'
    })
  }
})

export const { resetSuccessAndError } = gygFoodSafetyTagSlice.actions
export default gygFoodSafetyTagSlice.reducer
